import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, AlertTriangle, RefreshCw, LogOut, CheckCircle2 } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";

const GoogleIcon = () => (
  <svg className="w-4 h-4 mr-2.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
  </svg>
);

const EmailConfig = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [emailConfig, setEmailConfig] = useState({
    sender_email: "",
    sender_name: "",
    is_active: false,
    google_connected: false,
  });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ApiClient.request("/email/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setEmailConfig({
          sender_email: response.config.sender_email || "",
          sender_name: response.config.sender_name || "",
          is_active: response.config.is_active || false,
          google_connected: response.config.google_connected || false,
        });
      }
    } catch (error) {
      console.log("Email config not found");
    } finally {
      setLoading(false);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const toggleActive = async () => {
    if (!emailConfig.google_connected) {
      showToast("Please connect your Google Account first.", "error");
      return;
    }

    try {
      const newActiveState = !emailConfig.is_active;
      const response = await ApiClient.request("/email/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
      });

      if (response.success) {
        setEmailConfig((prev) => ({ ...prev, is_active: newActiveState }));
        showToast(`Email integration ${newActiveState ? "activated" : "deactivated"}!`, "success");
      }
    } catch (error) {
      // Handled globally
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const response = await ApiClient.request("/email/google/auth-url", {
        aiAgentId: activeAiAgentId,
      });

      if (response.success && response.auth_url) {
        window.location.href = response.auth_url;
      } else {
        showToast("Failed to initialize Google Authentication", "error");
      }
    } catch (error) {
      showToast("Error connecting to Google Service", "error");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect your Google account? The agent will no longer process support emails.")) {
      return;
    }

    try {
      setDisconnecting(true);
      const response = await ApiClient.request("/email/google/disconnect", {
        aiAgentId: activeAiAgentId,
        method: "POST",
      });

      if (response.success) {
        setEmailConfig({
          sender_email: "",
          sender_name: "",
          is_active: false,
          google_connected: false,
        });
        showToast("Google account disconnected successfully!", "success");
      }
    } catch (error) {
      showToast("Failed to disconnect account", "error");
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[360px]">
        <div className="w-8 h-8 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin mb-3" />
        <p className="text-xs font-medium text-zinc-400">Loading email configuration...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">
        
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/integrations")}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              title="Back to Integrations"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-center gap-2.5">
              <img src="/gmail.png" alt="Email" className="w-8 h-8 object-contain rounded-md flex-shrink-0" />
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Email & Gmail Sync</h1>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Monitor incoming customer support emails and dispatch autonomous AI replies.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-start sm:self-auto pl-9 sm:pl-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase border ${
              emailConfig.is_active && emailConfig.google_connected
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-500 border-zinc-200"
            }`}>
              {emailConfig.is_active && emailConfig.google_connected ? "Active" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={toggleActive}
              disabled={!emailConfig.google_connected}
              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                !emailConfig.google_connected
                  ? "bg-zinc-100 opacity-40 cursor-not-allowed"
                  : emailConfig.is_active
                  ? "bg-zinc-900"
                  : "bg-zinc-200"
              }`}
              title={
                !emailConfig.google_connected
                  ? "Connect Google Account first"
                  : emailConfig.is_active
                  ? "Deactivate integration"
                  : "Activate integration"
              }
            >
              <div
                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                  emailConfig.is_active && emailConfig.google_connected ? "left-4" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          {!emailConfig.google_connected ? (
            <div className="flex flex-col items-center text-center py-6 sm:py-8 space-y-4 max-w-md mx-auto">
              <Mail className="text-zinc-700 shrink-0" size={22} />
              <div>
                <h3 className="text-sm font-semibold text-zinc-900">Connect Your Google Account</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Authenticate your Gmail inbox securely via OAuth 2.0 to allow autonomous support parsing and drafted responses.
                </p>
              </div>

              <button
                onClick={handleConnect}
                disabled={connecting}
                className="inline-flex items-center justify-center px-4 py-2.5 bg-white border border-zinc-300 text-zinc-800 rounded-lg hover:bg-zinc-50 text-xs font-medium transition-colors shadow-xs"
              >
                {connecting ? (
                  <RefreshCw size={14} className="animate-spin text-zinc-500 mr-2" />
                ) : (
                  <GoogleIcon />
                )}
                <span>Sign in with Google</span>
              </button>

              <p className="text-[11px] text-zinc-400">
                You authorize EasyChat to read, draft, and reply to support emails. You can disconnect at any time.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Account details row */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-zinc-100">
                <div className="flex items-center gap-3">
                  <Mail className="text-emerald-600 shrink-0" size={18} />
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-xs font-semibold text-zinc-900">{emailConfig.sender_email}</h4>
                      <span className="px-1.5 py-0.2 text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
                        Connected
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      Display Name: <span className="font-medium text-zinc-800">{emailConfig.sender_name || "AI Agent"}</span>
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors self-start sm:self-auto disabled:opacity-40"
                >
                  {disconnecting ? <RefreshCw size={13} className="animate-spin" /> : <LogOut size={13} />}
                  <span>Disconnect Account</span>
                </button>
              </div>

              {/* Status Warning if offline */}
              {!emailConfig.is_active && (
                <div className="flex items-start gap-2.5 p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800">
                  <AlertTriangle size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p>
                    Account is connected but integration is currently <span className="font-semibold">Disabled</span>. Toggle the switch at the top right to start receiving and answering emails.
                  </p>
                </div>
              )}

              {/* Protocol features */}
              <div className="space-y-2.5 pt-1">
                <h4 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Features & Protocol</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div className="flex items-start gap-2 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                    <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h5 className="text-xs font-medium text-zinc-900">OAuth 2.0 Security</h5>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Direct Google authentication without storing raw account credentials.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                    <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h5 className="text-xs font-medium text-zinc-900">Context-Aware Replies</h5>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Drafts intelligent, accurate customer responses using agent intelligence.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                    <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h5 className="text-xs font-medium text-zinc-900">Background Email Sync</h5>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Processes incoming customer messages continuously on the server.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                    <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h5 className="text-xs font-medium text-zinc-900">Token Auto-Refresh</h5>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Maintains uninterrupted connection through automated token rotation.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailConfig;
